import { useState, useEffect, useRef } from 'react';
import { fetchSocialFeed } from '../services/socialFeedService';

const BlogSection = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [failedImageIds, setFailedImageIds] = useState(new Set());
  const hasFetched = useRef(false);

  useEffect(() => {
    // Prevent duplicate fetches in React StrictMode
    if (hasFetched.current) return;
    hasFetched.current = true;

    const loadSocialPosts = async () => {
      setLoading(true);
      const result = await fetchSocialFeed();

      if (result.success) {
        console.log('📱 Loaded', result.posts.length, 'social media posts');
        setPosts(result.posts);
        setError(null);
      } else {
        console.error('❌ Failed to load posts:', result.error);
        setError(result.error);
      }
      setLoading(false);
    };

    loadSocialPosts();
  }, []);

  // Handle image load error by removing the post
  const handleImageError = (postId) => {
    console.log(`⚠️ Image failed to load for post ${postId}, removing from display`);
    setFailedImageIds(prev => new Set([...prev, postId]));
  };

  // Filter out posts with failed images
  const displayedPosts = posts.filter(post => !failedImageIds.has(post.id));

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const truncateCaption = (caption, maxLength = 100) => {
    if (!caption) return '';
    if (caption.length <= maxLength) return caption;
    return caption.substring(0, maxLength) + '...';
  };

  // Helper function to darken/lighten colors
  const adjustColor = (color, amount) => {
    const clamp = (val) => Math.min(Math.max(val, 0), 255);
    const num = parseInt(color.replace('#', ''), 16);
    const r = clamp((num >> 16) + amount);
    const g = clamp(((num >> 8) & 0x00FF) + amount);
    const b = clamp((num & 0x0000FF) + amount);
    return '#' + (0x1000000 + (r << 16) + (g << 8) + b).toString(16).slice(1);
  };

  // Card styles
  const cardStyle = {
    borderRadius: '12px',
    overflow: 'hidden',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
    transition: 'all 0.3s ease',
    backgroundColor: '#fff',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  };

  const cardHoverStyle = {
    transform: 'translateY(-5px)',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
  };

  const imageContainerStyle = {
    position: 'relative',
    paddingBottom: '100%', // Square aspect ratio
    overflow: 'hidden',
    backgroundColor: '#f0f0f0',
  };

  const imageStyle = {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    transition: 'transform 0.3s ease',
  };

  const contentStyle = {
    padding: '20px',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
  };

  const metaStyle = {
    display: 'flex',
    gap: '15px',
    marginBottom: '12px',
    fontSize: '0.85rem',
    color: '#666',
    flexWrap: 'wrap',
  };

  const titleStyle = {
    fontSize: '1.1rem',
    fontWeight: '600',
    marginBottom: '10px',
    lineHeight: '1.4',
    color: '#333',
  };

  const descStyle = {
    fontSize: '0.9rem',
    color: '#666',
    lineHeight: '1.6',
    marginBottom: '15px',
    flex: 1,
  };

  const buttonStyle = {
    display: 'inline-block',
    padding: '10px 20px',
    backgroundColor: '#E1306C',
    color: '#fff',
    borderRadius: '6px',
    textDecoration: 'none',
    fontSize: '0.9rem',
    fontWeight: '500',
    transition: 'background-color 0.3s ease',
    textAlign: 'center',
  };

  const playIconStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '60px',
    height: '60px',
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '24px',
    color: '#fff',
  };

  return (
    <section className="prt-row bg-base-grey blog-section clearfix">
      <div className="container">
        {loading && (
          <div className="row">
            <div className="col-lg-12 text-center">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Cargando...</span>
              </div>
              <p className="mt-3">Cargando publicaciones...</p>
            </div>
          </div>
        )}

        {error && (
          <div className="row">
            <div className="col-lg-12">
              <div className="alert alert-warning text-center" role="alert">
                No se pudieron cargar las publicaciones. Por favor, inténtalo más tarde.
              </div>
            </div>
          </div>
        )}

        {!loading && !error && displayedPosts.length > 0 && (
          <div className="row g-4 mt-4">
            {displayedPosts.map((post) => (
              <div key={post.id} className="col-xl-3 col-lg-4 col-md-6 col-sm-12 mb-4">
                <div
                  style={cardStyle}
                  onMouseEnter={(e) => {
                    Object.assign(e.currentTarget.style, cardHoverStyle);
                    const img = e.currentTarget.querySelector('img');
                    if (img) img.style.transform = 'scale(1.05)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = cardStyle.boxShadow;
                    const img = e.currentTarget.querySelector('img');
                    if (img) img.style.transform = 'scale(1)';
                  }}
                >
                  <div style={imageContainerStyle}>
                    <a href={post.permalink} target="_blank" rel="noopener noreferrer">
                      <img
                        loading="lazy"
                        src={post.media_type === 'VIDEO' ? post.thumbnail_url : post.media_url}
                        alt={post.caption ? truncateCaption(post.caption, 50) : 'Social media post'}
                        style={imageStyle}
                        onError={(e) => {
                          e.target.onerror = null; // Prevent infinite loop
                          handleImageError(post.id);
                        }}
                      />
                    </a>
                    {post.media_type === 'VIDEO' && (
                      <div style={playIconStyle}>
                        <i className="fa fa-play"></i>
                      </div>
                    )}
                  </div>
                  <div style={contentStyle}>
                    <div style={metaStyle}>
                      <span>
                        <i className="fa fa-calendar" style={{ marginRight: '5px' }}></i>
                        {formatDate(post.timestamp)}
                      </span>
                      <span>
                        <i className={post.platformIcon || 'fa fa-rss'} style={{ marginRight: '5px' }}></i>
                        {post.platform || 'Social'}
                      </span>
                    </div>
                    <h3 style={titleStyle}>
                      {truncateCaption(post.caption, 80) || 'Ver publicación'}
                    </h3>
                    <p style={descStyle}>
                      {truncateCaption(post.caption, 150)}
                    </p>
                    <a
                      style={{
                        ...buttonStyle,
                        backgroundColor: post.platformColor || '#E1306C'
                      }}
                      href={post.permalink}
                      target="_blank"
                      rel="noopener noreferrer"
                      onMouseEnter={(e) => {
                        const color = post.platformColor || '#E1306C';
                        e.target.style.backgroundColor = adjustColor(color, -20);
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = post.platformColor || '#E1306C';
                      }}
                    >
                      Ver en {post.platform || 'Redes'}
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && !error && displayedPosts.length === 0 && (
          <div className="row">
            <div className="col-lg-12 text-center">
              <p>No hay publicaciones disponibles en este momento.</p>
            </div>
          </div>
        )}

        {!loading && !error && displayedPosts.length > 0 && (
          <div className="row mt-5">
            <div className="col-lg-12 text-center">
              <div style={{ display: 'flex', gap: '15px', justifyContent: 'center', flexWrap: 'wrap' }}>
                <a
                  href="https://www.instagram.com/equipomag"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '12px 28px',
                    backgroundColor: '#E1306C',
                    color: '#fff',
                    borderRadius: '50px',
                    textDecoration: 'none',
                    fontSize: '0.95rem',
                    fontWeight: '600',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 4px 15px rgba(225, 48, 108, 0.3)',
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#C13584';
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 6px 20px rgba(225, 48, 108, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = '#E1306C';
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 4px 15px rgba(225, 48, 108, 0.3)';
                  }}
                >
                  <i className="fa fa-instagram" style={{ fontSize: '1.2rem' }}></i>
                  Instagram
                </a>
                <a
                  href="https://www.tiktok.com/@equipomag"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '12px 28px',
                    backgroundColor: '#000000',
                    color: '#fff',
                    borderRadius: '50px',
                    textDecoration: 'none',
                    fontSize: '0.95rem',
                    fontWeight: '600',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 4px 15px rgba(0, 0, 0, 0.3)',
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#333333';
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 6px 20px rgba(0, 0, 0, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = '#000000';
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 4px 15px rgba(0, 0, 0, 0.3)';
                  }}
                >
                  <i className="fab fa-tiktok" style={{ fontSize: '1.2rem' }}></i>
                  TikTok
                </a>
                <a
                  href="https://www.youtube.com/@enbuscadelasalud"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '12px 28px',
                    backgroundColor: '#FF0000',
                    color: '#fff',
                    borderRadius: '50px',
                    textDecoration: 'none',
                    fontSize: '0.95rem',
                    fontWeight: '600',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 4px 15px rgba(255, 0, 0, 0.3)',
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#CC0000';
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 6px 20px rgba(255, 0, 0, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = '#FF0000';
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 4px 15px rgba(255, 0, 0, 0.3)';
                  }}
                >
                  <i className="fa fa-youtube" style={{ fontSize: '1.2rem' }}></i>
                  YouTube
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default BlogSection;
